import { hasFailed } from "@miragon/react-utils-api";
import React, { useCallback, useEffect, useState } from "react";
import QAButton from "../../components/Design/Controls/QAButton";
import QAButtonRow from "../../components/Design/Layout/QAButtonRow";
import useSnackbar from "../../components/Hooks/Snackbar/useSnackbar";
import SettingsForm from "../../components/Settings/Old/Form/SettingsForm";
import SettingsLabel from "../../components/Settings/Old/Form/SettingsLabel";
import SettingsTextField from "../../components/Settings/Old/Form/SettingsTextField";
import { useAppDispatch } from "../../state/Store";
import {
    ControllerAPI,
    CreateScenarioCommand,
    CreateScenarioCommandRole, CreateScenarioCommandVariable,
    RoleRO,
    ScenarioRO,
    UpdateScenarioCommand,
    UpdateScenarioCommandRole, UpdateScenarioCommandVariable
} from "../../api/api";
import { makeStyles } from "@mui/styles";
import { updateScenariosManually } from "../../state/ScenariosState";
import ScenarioRoleGroup from "./Form/ScenarioRoleGroup";
import VariableDefinitionForm from "../../components/Variables/VariableDefinitionForm";

interface Props {
    scenario: ScenarioRO | undefined;
    availableRoles: RoleRO[];
}

const useStyles = makeStyles({
    roleRows: {
        marginTop: "8px"
    }
});

const ScenarioForm: React.FC<Props> = props => {
    const snackbar = useSnackbar();
    const dispatch = useAppDispatch();
    const classes = useStyles();

    const { scenario, availableRoles } = props;

    const [group, setGroup] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [roles, setRoles] = useState<(CreateScenarioCommandRole & UpdateScenarioCommandRole)[]>([]);
    const [variables, setVariables] = useState<(CreateScenarioCommandVariable & UpdateScenarioCommandVariable)[]>([]);
    const [expanded, setExpanded] = useState<number>();
    const [isDisabled, setIsDisabled] = useState(true);

    useEffect(() => {
        if (scenario) {
            setGroup(scenario.group);
            setName(scenario.name);
            setDescription(scenario.description);
            setRoles(scenario.roles);
            setVariables(scenario.variables);
        }
        setIsDisabled(false);
    }, [scenario]);

    const onSave = useCallback(async () => {
        if (roles.find(r => !r.roleId || !r.name || r.instances.length <= 0)) {
            snackbar.onError("Bitte alle Rollen vollständig ausfüllen.");
            return;
        }

        const request: CreateScenarioCommand & UpdateScenarioCommand = { group, name, description, roles, variables };

        setIsDisabled(true);

        const response = scenario
            ? await ControllerAPI.updateScenario(scenario.id, request)
            : await ControllerAPI.createScenario(request);

        setIsDisabled(false);

        if (hasFailed(response)) {
            snackbar.onApiError(response);
        } else {
            dispatch(updateScenariosManually({
                update: [response.result]
            }));
            snackbar.onSuccess("Szenario gespeichert.");
        }
    }, [roles, group, name, description, variables, scenario, snackbar, dispatch]);

    return (
        <>

            <SettingsForm>

                <SettingsLabel>Allgemein</SettingsLabel>

                <SettingsTextField
                    label="Gruppe"
                    disabled={isDisabled}
                    onChanged={setGroup}
                    value={group} />

                <SettingsTextField
                    label="Name"
                    disabled={isDisabled}
                    onChanged={setName}
                    value={name} />

                <SettingsTextField
                    label="Beschreibung"
                    disabled={isDisabled}
                    onChanged={setDescription}
                    value={description} />

            </SettingsForm>

            <SettingsForm xlarge>

                <SettingsLabel>Rollen-Gruppen</SettingsLabel>

                <QAButtonRow alignRight>

                    <QAButton
                        light
                        size="tiny"
                        disabled={isDisabled}
                        title="Hinzufügen"
                        onClicked={() => {
                            setRoles(cur => [
                                ...cur,
                                {
                                    instances: [{
                                        name: "",
                                        variables: {}
                                    }],
                                    roleId: availableRoles[0].id,
                                    name: availableRoles[0].name
                                }
                            ]);
                            setTimeout(() => setExpanded(roles.length), 100);
                        }} />

                </QAButtonRow>

                <div className={classes.roleRows}>
                    {roles.map((role, index) => (
                        <ScenarioRoleGroup
                            key={index}
                            role={role}
                            onDelete={() => setRoles(cur => cur.filter((_, i) => i !== index))}
                            onUpdate={value => setRoles(cur => cur.map((r, i) => i === index ? value : r))}
                            availableRoles={availableRoles}
                            expanded={expanded === index}
                            scenarioVariables={variables}
                            onExpandedChanged={value => setExpanded(value ? index : undefined)}
                            disabled={isDisabled} />
                    ))}
                    {roles.length === 0 && (
                        <span>Keine Rollen zugewiesen.</span>
                    )}
                </div>

                <SettingsLabel>Variablen</SettingsLabel>

                <VariableDefinitionForm
                    variables={variables}
                    onUpdate={setVariables}
                    disabled={isDisabled} />

                <QAButtonRow marginTop>

                    <QAButton
                        disabled={isDisabled}
                        title="Speichern"
                        onClicked={onSave} />

                </QAButtonRow>

            </SettingsForm>

        </>
    );
};

export default ScenarioForm;
